import { useMemo, useState } from "react"
import Icon from "../../components/Icon"
import { formatDateWithTime, formatMoney } from "../../utils/format"
import { getApiErrorMessage } from "../../utils/errorMessage"
import StatementTable from "./StatementTable"
import PrepaidCreditPanel from "./PrepaidCreditPanel"
import { computeUtilityStatementRunningBalances, getUtilityStatementDate } from "../frp/frpStatements"
import UtilityInvoiceStatusBadge from "./UtilityInvoiceStatusBadge"
import {
  getUtilityDebtSummary,
  getUtilityRowChargeAmount,
  getUtilityRowPaymentAmount,
  getUtilitySettlementId,
  getUtilityWalletBalance,
  getVisibleUtilitySettlementRows,
} from "./utilitySettlement"

// History opens on the most recent records only. A tenant with years of bills
// should not have to scroll past all of them to reach the rest of the page.
const HISTORY_PAGE_SIZE = 5

// Sized to sit three-across on a phone: the three headline figures belong on one
// screen with the bills, not stacked down a scroll.
function StatCard({ icon, label, value, tone = "slate" }) {
  const tones = {
    teal: "from-teal-500 to-emerald-500",
    red: "from-rose-500 to-red-500",
    slate: "from-slate-600 to-slate-700",
  }
  const valueTones = {
    teal: "text-slate-900",
    red: "text-red-600",
    slate: "text-slate-900",
  }
  return (
    <div className="rounded-2xl border border-slate-200 bg-white px-2.5 py-2.5 shadow-sm sm:px-4 sm:py-4">
      <div className="flex items-start gap-1.5 sm:items-center sm:gap-2">
        <div className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md bg-gradient-to-br text-white sm:mt-0 sm:h-7 sm:w-7 sm:rounded-lg ${tones[tone]}`}>
          <Icon name={icon} size="0.65rem" />
        </div>
        <p className="text-[9px] font-bold uppercase leading-tight tracking-wide text-slate-400 sm:text-[11px]">
          {label}
        </p>
      </div>
      <p className={`mt-1.5 text-[15px] font-black leading-tight tracking-tight tabular-nums sm:mt-2 sm:text-2xl ${valueTones[tone]}`}>
        {value}
      </p>
    </div>
  )
}

function BillsView({
  wallet,
  topUpEnabled,
  invoices,
  outstandingTotal,
  utilityStatement,
  statementRows,
  statementSummary,
  onAddFunds,
  prepaymentCredit,
  onPrepaid,
}) {
  const [topUpAmount, setTopUpAmount] = useState("")
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState("")
  const [showHistory, setShowHistory] = useState(true)
  const [historyCount, setHistoryCount] = useState(HISTORY_PAGE_SIZE)
  const debtSummary = getUtilityDebtSummary(statementSummary, invoices)
  const statementInvoicesById = useMemo(() => new Map(
    [
      ...(Array.isArray(invoices) ? invoices : []),
      ...(Array.isArray(utilityStatement?.invoices) ? utilityStatement.invoices : []),
    ].map((invoice) => [String(invoice?._id || invoice?.invoiceId || ""), invoice]),
  ), [invoices, utilityStatement?.invoices])
  const invoiceForRow = (row) => statementInvoicesById.get(String(row?.invoiceId || "")) || row
  const amountDue = Number.isFinite(Number(outstandingTotal))
    ? Math.max(0, Number(outstandingTotal))
    : debtSummary.amountDue
  const visibleStatementRows = useMemo(
    () => getVisibleUtilitySettlementRows(statementRows),
    [statementRows],
  )
  const statementRowsWithRunningBalance = computeUtilityStatementRunningBalances(
    visibleStatementRows,
    debtSummary.netDebtBalance,
  )
  // Balances are carried backwards from today, so the whole statement must be
  // calculated before it is cut down to the newest rows.
  const shownStatementRows = statementRowsWithRunningBalance.slice(0, historyCount)
  const hiddenStatementCount = statementRowsWithRunningBalance.length - shownStatementRows.length

  const submitTopUp = async (event) => {
    event.preventDefault()
    if (busy) return
    setBusy(true)
    setError("")
    try {
      await onAddFunds(topUpAmount)
      setTopUpAmount("")
    } catch (err) {
      setError(getApiErrorMessage(err, "Failed to top up."))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="space-y-5">
      <section className={`grid gap-2 sm:gap-3 ${debtSummary.creditAmount > 0 ? "grid-cols-3" : "grid-cols-2"}`}>
        <StatCard icon="exclamation-circle" label="Amount due" value={formatMoney(amountDue)} tone={amountDue > 0 ? "red" : "slate"} />
        <StatCard icon="wallet2" label="Available in wallet" value={formatMoney(getUtilityWalletBalance(wallet || {}))} tone="teal" />
        {debtSummary.creditAmount > 0 && (
          <StatCard icon="check-circle" label="Account credit" value={formatMoney(debtSummary.creditAmount)} tone="teal" />
        )}
      </section>

      <PrepaidCreditPanel prepaymentCredit={prepaymentCredit} onPrepaid={onPrepaid} />

      {topUpEnabled && (
        <section className="rounded-2xl border border-teal-200 bg-gradient-to-br from-teal-50 to-emerald-50 p-4 shadow-sm">
          <form onSubmit={submitTopUp} className="flex flex-col gap-3 sm:flex-row sm:items-end">
            <div className="flex-1">
              <label className="flex items-center gap-1.5 text-sm font-bold text-slate-700">
                <Icon name="plus-circle-fill" size="0.9rem" className="text-teal-600" />
                Top up your wallet
              </label>
              <input
                type="number"
                min="0.01"
                step="0.01"
                value={topUpAmount}
                onChange={(event) => setTopUpAmount(event.target.value)}
                className="mt-1.5 w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-100"
                placeholder="Amount"
                required
              />
            </div>
            <button
              type="submit"
              disabled={busy}
              className="rounded-xl bg-teal-600 px-5 py-2.5 text-sm font-bold text-white transition-colors hover:bg-teal-700 disabled:opacity-60"
            >
              {busy ? "Processing…" : "Top up"}
            </button>
          </form>
          {error && <p className="mt-2 text-xs font-medium text-red-600">{error}</p>}
        </section>
      )}

      {/* History */}
      <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <button
          type="button"
          onClick={() => {
            setShowHistory((value) => !value)
            setHistoryCount(HISTORY_PAGE_SIZE)
          }}
          className="flex w-full items-center justify-between px-4 py-3 text-left"
        >
          <span className="flex items-center gap-2 text-base font-black text-slate-900">
            <Icon name="clock-history" size="1rem" className="text-teal-600" />
            Statement &amp; History
          </span>
          <Icon name={showHistory ? "chevron-up" : "chevron-down"} size="0.9rem" className="text-slate-400" />
        </button>

        {showHistory && (
          <div className="border-t border-slate-100">
            {visibleStatementRows.length === 0 ? (
              <EmptyState icon="clock-history" title="No history yet" message="Charges and payments will show here." />
            ) : (
              <div className="border-t border-emerald-100">
                <div className="flex flex-wrap items-center gap-2 px-4 py-3">
                  <div className="inline-flex rounded-full bg-emerald-50 px-2.5 py-1 text-[10px] font-semibold text-emerald-700 sm:text-xs">
                    {visibleStatementRows.length} record{visibleStatementRows.length === 1 ? "" : "s"}
                  </div>
                  {hiddenStatementCount > 0 && (
                    <span className="text-[10px] font-medium text-slate-400 sm:text-xs">
                      showing the latest {shownStatementRows.length}
                    </span>
                  )}
                </div>
                <StatementTable
                  rows={shownStatementRows}
                  rowKey={(row, index) => `${row.type}-${getUtilitySettlementId(row) || row.invoiceId || index}-${row.date || index}`}
                  emptyText="No history yet."
                  columns={[
                    {
                      label: "Date",
                      width: "19%",
                      render: (row) => formatDateWithTime(getUtilityStatementDate(row)),
                    },
                    {
                      label: "Description",
                      align: "left",
                      render: (row) => (
                        <div>
                          <div className="font-medium text-slate-900 whitespace-pre-line">{row.description || "Utility record"}</div>
                          {row.providerName && (
                            <div className="mt-0.5 text-slate-500">{row.providerName}</div>
                          )}
                          <div className="mt-0.5 text-slate-400 capitalize">{row.type}</div>
                          {row?.type === "invoice" && (
                            <div className="mt-1">
                              <UtilityInvoiceStatusBadge invoice={invoiceForRow(row)} compact />
                            </div>
                          )}
                          {row?.source === "water_meter" && row?.meta && (
                            <div className="text-slate-400">
                              {Number(row.meta.currentReading || 0).toFixed(2)}→{Number(row.meta.cutOffReading || 0).toFixed(2)} ({Number(row.meta.allowedUnits || 0).toFixed(2)} u)
                            </div>
                          )}
                        </div>
                      ),
                    },
                    {
                      label: "Charge",
                      width: "15%",
                      align: "right",
                      render: (row) =>
                        getUtilityRowChargeAmount(row) > 0 ? (
                          <span className="font-semibold text-red-600">{formatMoney(getUtilityRowChargeAmount(row))}</span>
                        ) : (
                          <span className="text-slate-400">-</span>
                        ),
                    },
                    {
                      label: "Payment",
                      width: "15%",
                      align: "right",
                      render: (row) =>
                        getUtilityRowPaymentAmount(row) > 0 ? (
                          <span className="font-semibold text-emerald-700">{formatMoney(getUtilityRowPaymentAmount(row))}</span>
                        ) : (
                          <span className="text-slate-400">-</span>
                        ),
                    },
                    {
                      label: "Balance",
                      width: "16%",
                      align: "right",
                      render: (row) => <span className="font-semibold text-slate-700">{formatMoney(row.runningBalance || 0)}</span>,
                    },
                  ]}
                />

                {(hiddenStatementCount > 0 || historyCount > HISTORY_PAGE_SIZE) && (
                  <div className="flex items-center justify-center gap-2 border-t border-slate-100 bg-slate-50 px-4 py-2.5">
                    {hiddenStatementCount > 0 && (
                      <button
                        type="button"
                        onClick={() => setHistoryCount((count) => count + HISTORY_PAGE_SIZE)}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-teal-200 bg-white px-3 py-1.5 text-xs font-bold text-teal-700 transition-colors hover:bg-teal-50"
                      >
                        <Icon name="chevron-down" size="0.65rem" />
                        Show {Math.min(HISTORY_PAGE_SIZE, hiddenStatementCount)} more
                      </button>
                    )}
                    {historyCount > HISTORY_PAGE_SIZE && (
                      <button
                        type="button"
                        onClick={() => setHistoryCount(HISTORY_PAGE_SIZE)}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-bold text-slate-600 transition-colors hover:bg-slate-100"
                      >
                        <Icon name="chevron-up" size="0.65rem" />
                        Show less
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </section>
    </div>
  )
}

function EmptyState({ icon, title, message }) {
  return (
    <div className="flex flex-col items-center gap-2 px-4 py-12 text-center">
      <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-100 text-slate-400">
        <Icon name={icon} size="1.4rem" />
      </div>
      <p className="text-sm font-semibold text-slate-700">{title}</p>
      <p className="max-w-xs text-xs text-slate-500">{message}</p>
    </div>
  )
}

export default BillsView
